import React, { ChangeEvent, useState } from "react";
import {
  Form,
  Input,
  Select,
  Button,
  Tabs,
  Popconfirm,
  Typography,
  Divider,
  Upload,
  Tag,
} from "antd";
import {
  PlusOutlined,
  UploadOutlined,
  DeleteOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
} from "@ant-design/icons";
import { CourseEntityInput } from "../../../../services/courses/courses.types";
import { CourseMinimalResponse } from "../../../../types/api/getCourse";
import { LessonMinimalResponse } from "../../../../types/api/getLesson";
import { ProjectPhase } from "../../../../services/projects/projects.types";
import { ProgramEntity } from "../../../../services/programs/programs.types";
import type { UploadRequestOption } from "rc-upload/lib/interface";
import ResourcePreview from "../../../common/ResourcePreview";


const { TextArea } = Input;
const { Title } = Typography;
const { Option } = Select;

interface ProjectRubric {
  rubric_achieved: string;
  rubric_in_process: string;
  rubric_needs_support: string;
  key_learnings: string;
}

interface LessonWithPhase {
  id: number;
  title: string;
  phase_id?: number;
}

interface SelectedCourse {
  id: number;
  title: string;
  program_id?: number | null;
  week?: number | null;
}

interface ProjectCourseFormProps {
  initialValues: CourseEntityInput;
  setInitialValues: React.Dispatch<React.SetStateAction<CourseEntityInput>>;
  selectedCourses: SelectedCourse[];
  setSelectedCourses: React.Dispatch<React.SetStateAction<SelectedCourse[]>>;
  selectedLessons: LessonWithPhase[];
  setSelectedLessons: React.Dispatch<React.SetStateAction<LessonWithPhase[]>>;
  projectRubrics: ProjectRubric[];
  setProjectRubrics: React.Dispatch<React.SetStateAction<ProjectRubric[]>>;
  courses: CourseMinimalResponse[];
  availableLessons: LessonMinimalResponse[];
  projectPhases: ProjectPhase[];
  setProjectPhases: React.Dispatch<React.SetStateAction<ProjectPhase[]>>;
  uploadingProjectImage: boolean;
  projectImageUrl: string | null;
  handleProjectImageUpload: (options: UploadRequestOption) => Promise<void>;
  handleRemoveCourse: (index: number) => void;
  handleAddCourseAtIndex: (index: number) => void;
  handleCourseChange: (index: number, courseId: number) => void;
  handleRemoveLesson: (index: number) => void;
  handleAddLessonAtIndex: (index: number) => void;
  handleLessonChange: (index: number, field: string, value: string | number | undefined) => void;
  handleAddProjectRubric: () => void;
  handleRemoveProjectRubric: (index: number) => void;
  handleProjectRubricChange: (
    index: number,
    field: string,
    value: string,
  ) => void;
  handlePhaseTitleChange: (phaseId: number, title: string) => void;
  programs?: ProgramEntity[];
  programsbyCourse?: { [courseId: number]: ProgramEntity[] };
  handleCourseReorder?: (newCourses: SelectedCourse[]) => void;
}

const ProjectCourseForm: React.FC<ProjectCourseFormProps> = ({
  initialValues,
  setInitialValues,
  selectedCourses,
  setSelectedCourses,
  selectedLessons,
  setSelectedLessons,
  projectRubrics,
  courses,
  availableLessons,
  projectPhases,
  uploadingProjectImage,
  projectImageUrl,
  handleProjectImageUpload,
  handleRemoveCourse,
  handleAddCourseAtIndex,
  handleCourseChange,
  handleRemoveLesson,
  handleAddLessonAtIndex,
  handleLessonChange,
  handleAddProjectRubric,
  handleRemoveProjectRubric,
  handleProjectRubricChange,
  handlePhaseTitleChange,
  programs = [],
  programsbyCourse = {},
  handleCourseReorder,
}) => {
  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newCourses = [...selectedCourses];
    [newCourses[index - 1], newCourses[index]] = [newCourses[index], newCourses[index - 1]];
    setSelectedCourses(newCourses);
    if (handleCourseReorder) handleCourseReorder(newCourses);
  };

  const handleMoveDown = (index: number) => {
    if (index === selectedCourses.length - 1) return;
    const newCourses = [...selectedCourses];
    [newCourses[index], newCourses[index + 1]] = [newCourses[index + 1], newCourses[index]];
    setSelectedCourses(newCourses);
    if (handleCourseReorder) handleCourseReorder(newCourses);
  };

  const handleProgramChange = (index: number, programId: number | null) => {
    const updatedCourses = [...selectedCourses];
    const program = programId
      ? (programsbyCourse[updatedCourses[index].id] ?? []).find((p) => p.id === programId)
      : undefined;
    updatedCourses[index] = {
      ...updatedCourses[index],
      program_id: programId,
      week: program?.week ?? null,
    };
    setSelectedCourses(updatedCourses);
  };
  return (
    <>
      <Form.Item label="Adjuntar imagen" help="Imagen del proyecto (opcional)">
        <Upload
          customRequest={handleProjectImageUpload}
          showUploadList={false}
          disabled={uploadingProjectImage}
          accept="image/*"
        >
          <Button icon={<UploadOutlined />} loading={uploadingProjectImage}>
            {projectImageUrl ? "Reemplazar imagen" : "Subir imagen"}
          </Button>
        </Upload>
        {initialValues.file_id && (
          <p style={{ marginTop: "8px", color: "#1890ff" }}>
            📄 {initialValues.file_id}
          </p>
        )}
      </Form.Item>

      {projectImageUrl && (
        <>
          <div style={{ marginBottom: "16px" }}>
            <ResourcePreview
              fileId={
                initialValues.file_id ||
                projectImageUrl.split("?")[0].split("/").pop() ||
                "image.webp"
              }
              fileUrl={projectImageUrl}
            />
          </div>
        </>
      )}

      <div style={{ marginBottom: "24px" }} />

      <Form.Item label="Pregunta detonadora" name="trigger_question">
        <TextArea
          rows={2}
          value={initialValues.trigger_question}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
            setInitialValues({
              ...initialValues,
              trigger_question: e.target.value,
            })
          }
        />
      </Form.Item>

      <Form.Item label="Producto Final" name="final_product">
        <TextArea
          rows={2}
          value={initialValues.final_product}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
            setInitialValues({
              ...initialValues,
              final_product: e.target.value,
            })
          }
        />
      </Form.Item>

      <Form.Item label="Resumen" name="summary">
        <TextArea
          rows={3}
          placeholder="Un proyecto"
          value={initialValues.summary}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
            setInitialValues({
              ...initialValues,
              summary: e.target.value,
            })
          }
        />
      </Form.Item>

      <Form.Item
        label="De lo humano a lo comunitario"
        name="human_to_community"
      >
        <TextArea
          rows={3}
          placeholder="Descripción de lo humano a lo comunitario"
          value={initialValues.human_to_community}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
            setInitialValues({
              ...initialValues,
              human_to_community: e.target.value,
            })
          }
        />
      </Form.Item>

      <Form.Item label="Ejes articuladores" name="ejes_articuladores">
        <Select
          mode="multiple"
          value={initialValues.ejes_articuladores || []}
          onChange={(values: string[]) =>
            setInitialValues({
              ...initialValues,
              ejes_articuladores: values,
            })
          }
        >
          <Option value="Inclusión">Inclusión</Option>
          <Option value="Pensamiento crítico">Pensamiento crítico</Option>
          <Option value="Interculturalidad crítica">
            Interculturalidad crítica
          </Option>
          <Option value="Igualdad de género">Igualdad de género</Option>
          <Option value="Vida saludable">Vida saludable</Option>
          <Option value="Apropiación de las culturas a través de la lectura y la escritura">
            Apropiación de las culturas a través de la lectura y la escritura
          </Option>
          <Option value="Artes y experiencias estéticas">
            Artes y experiencias estéticas
          </Option>
          <Option value="Cuidado del medio ambiente">
            Cuidado del medio ambiente
          </Option>
          <Option value="Fomento a la cultura de paz">
            Fomento a la cultura de paz
          </Option>
        </Select>
      </Form.Item>

      <Form.Item label="Habilidades siglo XXI" name="habilidades_siglo_xxi">
        <Select
          mode="multiple"
          value={initialValues.habilidades_siglo_xxi || []}
          onChange={(values: string[]) =>
            setInitialValues({
              ...initialValues,
              habilidades_siglo_xxi: values,
            })
          }
        >
          <Option value="Pensamiento crítico">Pensamiento crítico</Option>
          <Option value="Creatividad">Creatividad</Option>
          <Option value="Comunicación">Comunicación</Option>
          <Option value="Colaboración">Colaboración</Option>
          <Option value="Curiosidad">Curiosidad</Option>
          <Option value="Iniciativa">Iniciativa</Option>
          <Option value="Persistencia">Persistencia</Option>
          <Option value="Liderazgo">Liderazgo</Option>
          <Option value="Sensibilidad social y cultural">
            Sensibilidad social y cultural
          </Option>
          <Option value="Adaptabilidad">Adaptabilidad</Option>
        </Select>
      </Form.Item>

      <Divider />

      {/* Fases del proyecto - Títulos editables */}
      <div style={{ marginBottom: "24px" }}>
        <Title level={5}>Fases del Proyecto</Title>
        <p style={{ marginBottom: "16px", color: "var(--bo-text-secondary)", fontSize: "14px" }}>
          Personaliza los nombres de las fases del proyecto. Estas fases se
          utilizarán para organizar las lecciones.
        </p>
        {projectPhases
          .sort((a, b) => a.order_by - b.order_by)
          .map((phase) => (
            <Form.Item
              key={phase.id > 0 ? phase.id : `phase-${phase.phase_type}`}
              label={`Fase ${phase.order_by}`}
              style={{ marginBottom: "12px" }}
            >
              <Input
                value={phase.title}
                onChange={(e) =>
                  handlePhaseTitleChange(phase.id, e.target.value)
                }
                placeholder={`Nombre de la fase ${phase.order_by}`}
              />
            </Form.Item>
          ))}
      </div>

      <Divider />

      {/* Cursos trabajados */}
      <div style={{ marginBottom: "24px" }}>
        <Title level={5}>Cursos trabajados</Title>

        {selectedCourses.map((course, index) => (
          <div
            key={index}
            style={{
              display: "flex",
              gap: "6px",
              alignItems: "center",
              marginBottom: "6px",
              flexWrap: "wrap",
            }}
          >
            <span style={{ color: "#bfbfbf", fontSize: "11px", minWidth: "14px", textAlign: "center", flexShrink: 0 }}>
              {index + 1}
            </span>

            <Button.Group size="small">
              <Button
                icon={<ArrowUpOutlined />}
                disabled={index === 0}
                onClick={() => handleMoveUp(index)}
                title="Mover arriba"
              />
              <Button
                icon={<ArrowDownOutlined />}
                disabled={index === selectedCourses.length - 1}
                onClick={() => handleMoveDown(index)}
                title="Mover abajo"
              />
            </Button.Group>

            <Select
              size="small"
              style={{ flex: 2, minWidth: "160px" }}
              value={course.id || undefined}
              onChange={(value) => handleCourseChange(index, value)}
              placeholder="Selecciona un curso"
              showSearch
              optionFilterProp="children"
            >
              {[...courses]
                .sort((a, b) => b.id - a.id)
                .map((c) => (
                  <Option key={c.id} value={c.id}>
                    {c.id} - {c.title}
                  </Option>
                ))}
            </Select>

            {course.id > 0 && (
              <Select
                size="small"
                style={{ flex: 1, minWidth: "140px" }}
                value={course.program_id ?? undefined}
                onChange={(value) => handleProgramChange(index, value ?? null)}
                placeholder="Programa"
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {(programsbyCourse[course.id] ?? []).map((program) => (
                  <Option key={program.id} value={program.id}>
                    {program.title}
                  </Option>
                ))}
              </Select>
            )}

            {course.program_id && (
                <Tag color="blue" style={{ margin: 0 }}>Sem. {course.week ?? "-"}</Tag>
            )}

            <Popconfirm
              title="¿Eliminar este curso?"
              description="Se eliminará el curso de la lista del proyecto."
              onConfirm={() => handleRemoveCourse(index)}
              okText="Sí"
              cancelText="No"
            >
              <Button
                type="text"
                danger
                size="small"
                icon={<DeleteOutlined />}
                title="Eliminar curso"
                style={{ flexShrink: 0 }}
              />
            </Popconfirm>
          </div>
        ))}

        <Button
          type="dashed"
          onClick={() =>
            setSelectedCourses([
              ...selectedCourses,
              { id: 0, title: "", program_id: null, week: null },
            ])
          }
          icon={<PlusOutlined />}
          style={{ width: "100%", marginTop: selectedCourses.length > 0 ? "6px" : "0" }}
          block
        >
          Agregar curso
        </Button>
      </div>

      {/* Lecciones - Lista dinámica con selector de fase */}
      <div style={{ marginBottom: "24px" }}>
        <Title level={5}>Lecciones del proyecto</Title>
        {selectedLessons.map((lesson, index) => (
          <div
            key={index}
            style={{
              display: "flex",
              gap: "8px",
              marginBottom: "8px",
              alignItems: "center",
            }}
          >
            <Select
              style={{ flex: 2 }}
              value={lesson.id || undefined}
              onChange={(value) => handleLessonChange(index, "id", value)}
              placeholder="Selecciona una lección"
            >
              {[...availableLessons]
                .sort((a, b) => b.id - a.id)
                .map((l) => (
                  <Option key={l.id} value={l.id}>
                    {l.id} - {l.title}
                  </Option>
                ))}
            </Select>
            <Select
              style={{ flex: 1 }}
              value={lesson.phase_id || undefined}
              onChange={(value) => handleLessonChange(index, "phase_id", value)}
              placeholder="Selecciona una fase"
            >
              {projectPhases
                .sort((a, b) => a.order_by - b.order_by)
                .map((phase) => (
                  <Option
                    key={phase.id > 0 ? phase.id : `phase-${phase.phase_type}`}
                    value={phase.id}
                  >
                    Fase {phase.order_by}: {phase.title}
                  </Option>
                ))}
            </Select>
            <Popconfirm
              title="¿Eliminar esta lección?"
              description="Se eliminará la lección de la lista del proyecto."
              onConfirm={() => handleRemoveLesson(index)}
              okText="Sí"
              cancelText="No"
            >
              <Button danger>
                Eliminar
              </Button>
            </Popconfirm>
            <Button
              type="dashed"
              onClick={() => handleAddLessonAtIndex(index + 1)}
              icon={<PlusOutlined />}
            >
              Agregar abajo
            </Button>
          </div>
        ))}
        {selectedLessons.length === 0 && (
          <Button
            type="dashed"
            onClick={() =>
              setSelectedLessons([
                ...selectedLessons,
                { id: 0, title: "", phase_id: undefined },
              ])
            }
            icon={<PlusOutlined />}
            style={{ width: "100%" }}
          >
            Agregar lección
          </Button>
        )}
      </div>

      {/* SECCIÓN DE RÚBRICAS - Para Proyectos */}
      <div style={{ marginBottom: "24px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
          }}
        >
          <Title level={4}>Rúbricas</Title>
          <Button
            type="dashed"
            onClick={handleAddProjectRubric}
            icon={<PlusOutlined />}
            disabled={projectRubrics.length >= 10}
          >
            Agregar Rúbrica
          </Button>
        </div>

        <Tabs
          type="card"
          items={projectRubrics.map((rubric, index) => ({
            key: index.toString(),
            label: `${index + 1}`,
            children: (
              <div key={index}>
                <div className="rubricSection">
                  {/* Aprendizaje Clave */}
                  <div style={{ marginBottom: "16px" }}>
                    <div className="rubricLabel">Aprendizaje clave</div>
                    <Form.Item
                      name={["projectRubrics", index, "key_learnings"]}
                      style={{ marginBottom: 0 }}
                    >
                      <TextArea
                        rows={2}
                        placeholder="Describe el aprendizaje clave que se evalúa con esta rúbrica"
                        value={rubric.key_learnings}
                        onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                          handleProjectRubricChange(
                            index,
                            "key_learnings",
                            e.target.value,
                          )
                        }
                      />
                    </Form.Item>
                  </div>

                  <div className="rubricRow">
                    <div className="rubricItem">
                      <div className="rubricLabel">Rúbrica: logrado</div>
                      <Form.Item
                        name={["projectRubrics", index, "rubric_achieved"]}
                        style={{ marginBottom: 0 }}
                      >
                        <TextArea
                          rows={4}
                          value={rubric.rubric_achieved}
                          onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                            handleProjectRubricChange(
                              index,
                              "rubric_achieved",
                              e.target.value,
                            )
                          }
                        />
                      </Form.Item>
                    </div>
                    <div className="rubricItem">
                      <div className="rubricLabel">Rúbrica: en proceso</div>
                      <Form.Item
                        name={["projectRubrics", index, "rubric_in_process"]}
                        style={{ marginBottom: 0 }}
                      >
                        <TextArea
                          rows={4}
                          value={rubric.rubric_in_process}
                          onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                            handleProjectRubricChange(
                              index,
                              "rubric_in_process",
                              e.target.value,
                            )
                          }
                        />
                      </Form.Item>
                    </div>
                    <div className="rubricItem">
                      <div className="rubricLabel">Rúbrica: requiere apoyo</div>
                      <Form.Item
                        name={["projectRubrics", index, "rubric_needs_support"]}
                        style={{ marginBottom: 0 }}
                      >
                        <TextArea
                          rows={4}
                          value={rubric.rubric_needs_support}
                          onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                            handleProjectRubricChange(
                              index,
                              "rubric_needs_support",
                              e.target.value,
                            )
                          }
                        />
                      </Form.Item>
                    </div>
                  </div>
                </div>

                {projectRubrics.length > 1 && (
                  <Popconfirm
                    title="¿Eliminar esta rúbrica?"
                    description="Esta acción no se puede deshacer"
                    onConfirm={() => handleRemoveProjectRubric(index)}
                    okText="Sí"
                    cancelText="No"
                  >
                    <Button danger style={{ marginTop: "8px" }}>
                      Eliminar Rúbrica
                    </Button>
                  </Popconfirm>
                )}
              </div>
            ),
          }))}
        />
      </div>
    </>
  );
};

export default ProjectCourseForm;
